import { ChangeEvent, useEffect, useState } from 'react';
import { CampanhaValues } from 'models/campanhas';
import {
  CampaignLgpdReturn,
  CampaignSelectOption,
  CampanhaHookReturn,
} from 'models/campaign-mkt';
import { UseCampanhaContext } from 'context/campanha-context';
import campanhaService from 'services/campanha-service/campanha-service';

export const UseCampanhaHook = (): CampanhaHookReturn => {
  const {
    appliedFilters,
    currentFilters,
    dispatch,
    formCampanhaErrors,
    searchName,
    setAppliedFilters,
    setCurrentFilters,
    setFilterOpened,
    setFormCampanhaErrors,
    setPage,
    setRefreshList,
    setSearchName,
  } = UseCampanhaContext();

  const onFilterClicked = (isOpen: boolean) => {
    setFilterOpened(isOpen);
  };

  const applyFilter = (filter: Record<string, string>) => {
    setAppliedFilters({ ...appliedFilters, ...filter });
  };

  const onFilter = () => {
    setCurrentFilters(appliedFilters);
    setPage(1);
    setRefreshList(true);
  };

  const cleanFilter = () => {
    setSearchName('');
    setAppliedFilters({});
    setCurrentFilters({});
    setPage(1);
    setRefreshList(true);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchName(value);

    if (value !== currentFilters?.nome) {
      setCurrentFilters({ ...(currentFilters || {}), nome: value });
    }

    setRefreshList(true);
  };

  const handleInputError = (errorFieldName: string, value: boolean) => {
    const tempErrorValues = { ...formCampanhaErrors };

    tempErrorValues[errorFieldName] = value;
    setFormCampanhaErrors(tempErrorValues);
  };

  const handleDropdownChange = (
    target: string,
    resource: CampaignSelectOption[],
    inputName: string,
  ) => {
    const selected = resource.find((value) => {
      return value.id === target;
    });

    if (!selected) return;

    if (inputName === CampanhaValues.DURACAO) {
      dispatch({
        type: 'INPUT_CHANGE',
        input: { name: inputName, value: selected.id },
      });
    } else {
      dispatch({
        type: 'INPUT_CHANGE',
        input: { name: inputName, value: selected.label },
      });
    }
    handleInputError(inputName, false);
  };

  const handleInputChange = (inputName: string, value: string) => {
    dispatch({
      type: 'INPUT_CHANGE',
      input: { name: inputName, value },
    });
  };

  return {
    applyFilter,
    cleanFilter,
    handleDropdownChange,
    handleInputChange,
    handleInputError,
    handleSearchChange,
    onFilter,
    onFilterClicked,
    searchName,
  };
};

export const UseCampaignLgpd = (): CampaignLgpdReturn => {
  const { setIsLoading } = UseCampanhaContext();
  const [showLgpdModal, setShowLgpdModal] = useState(false);

  const handleAcceptLgpd = async () => {
    try {
      setIsLoading(true);
      await campanhaService.acceptLgpd();
      setShowLgpdModal(false);
    } catch {
      setShowLgpdModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkLgpd = async () => {
      try {
        setIsLoading(true);
        const response = await campanhaService.validateLgpd();
        setShowLgpdModal(!response);
      } catch {
        setShowLgpdModal(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkLgpd();
  }, [setIsLoading]);

  return {
    handleAcceptLgpd,
    showLgpdModal,
  };
};
